import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { syncEngine } from '@/lib/sync-engine';
import { useAuth } from '@/hooks/use-auth';
import { useTenant } from '@/contexts/tenant-context';
import type { SyncEngineStatus } from '@/types/sync';

interface SyncState {
  /** Current sync engine status */
  status: SyncEngineStatus;
  /** Whether the device is online */
  isOnline: boolean;
  /** Trigger an immediate sync */
  triggerSync: () => Promise<void>;
  /** Retry all failed sync items */
  retryFailed: () => Promise<void>;
}

const defaultStatus: SyncEngineStatus = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  isSyncing: false,
  pendingSyncCount: 0,
  pendingMediaCount: 0,
  failedCount: 0,
};

const SyncContext = createContext<SyncState | undefined>(undefined);

/**
 * SyncProvider manages the background sync engine lifecycle and
 * provides reactive sync status to the UI.
 */
export function SyncProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { activeTenantId } = useTenant();
  const [status, setStatus] = useState<SyncEngineStatus>(defaultStatus);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const scopeKey = user?.uid && activeTenantId
    ? `${user.uid}:${activeTenantId}`
    : 'anonymous';

  useEffect(() => {
    // Register status change handler — SyncEngine already propagates
    // online/offline status via this callback, so no need for duplicate listeners
    syncEngine.onStatusChange((newStatus) => {
      setStatus(newStatus);
      setIsOnline(newStatus.isOnline);
    });

    // Start the sync engine
    syncEngine.start();

    // Initial status fetch
    syncEngine.getStatus().then(setStatus);

    return () => {
      syncEngine.stop();
    };
  }, [scopeKey]);

  const triggerSync = useCallback(async () => {
    await syncEngine.processSync();
  }, []);

  const retryFailed = useCallback(async () => {
    await syncEngine.retryFailed();
  }, []);

  const value = useMemo<SyncState>(
    () => ({ status, isOnline, triggerSync, retryFailed }),
    [status, isOnline, triggerSync, retryFailed],
  );

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

/**
 * Hook to access sync state. Must be used within a SyncProvider.
 */
export function useSyncContext(): SyncState {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSyncContext must be used within a SyncProvider');
  }
  return context;
}

export { SyncContext };
